import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  Bot,
  CalendarClock,
  Check,
  CheckCircle2,
  Circle,
  Copy,
  FileText,
  ListChecks,
  Loader2,
  Sparkles,
  Users,
  Video,
} from 'lucide-react';
import type { Meeting } from '../types';
import type { CalendarEventDTO } from '../global';
import { useTodayAgenda } from '../features/digest/useTodayAgenda';
import { BOT_TONE_CLASS, botStatusLabel } from '../features/bot/botStatus';
import { isBotJoinable, wantsAutoJoin } from '../../../src/shared/autoJoin';
import {
  cardClass,
  formatClock,
  formatDate,
  pageTitle,
  primaryButton,
  secondaryButton,
  sectionTitle,
} from './ui/styles';
import { displaySummaryText } from '../features/recording/summaryRules';
import { dateLocale, plural, t, useLanguage } from '../lib/i18n';

interface DailyDigestViewProps {
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  isDarkMode: boolean;
  onAskAI: (prompt: string) => void;
  onUpdateMeeting: (meeting: Meeting) => void;
  onNotify: (kind: 'success' | 'error' | 'info', text: string) => void;
}

function dayKey(date: Date): string {
  return date.toDateString();
}

/** Takvimde Meet/Zoom/Teams linki olan, henüz bitmemiş etkinlik — bot gönderilebilir. */
function canSendBot(event: CalendarEventDTO): boolean {
  return Boolean(event.conference && event.conference.provider !== 'other') && Date.parse(event.endISO) > Date.now();
}

/**
 * Günlük Özet — kullanıcının bugün odaklanması gerekenler tek ekranda:
 *  1. Bugünün programı: takvim toplantıları ve botun katılım durumu.
 *  2. Dün deşifre edilen toplantıların kısa yapay zekâ özetleri.
 *  3. Tüm toplantılardan kalan açık aksiyon adımları (işaretlenebilir).
 */
export const DailyDigestView: React.FC<DailyDigestViewProps> = ({
  meetings,
  onSelectMeeting,
  isDarkMode,
  onAskAI,
  onUpdateMeeting,
  onNotify,
}) => {
  const { language } = useLanguage();
  const agenda = useTodayAgenda(true);
  const autoJoinOn = agenda.prefs.autoJoinBot;
  const [copied, setCopied] = useState(false);
  const [busyEventId, setBusyEventId] = useState<string | null>(null);
  const [showAllActions, setShowAllActions] = useState(false);
  const card = cardClass(isDarkMode);

  const meetingById = useMemo(() => new Map(meetings.map((m) => [m.id, m])), [meetings]);

  // — 1. Bugünün programı —
  // Takvimden gelmeyen (link yapıştırılarak) bugüne planlanmış bot toplantıları da listede.
  const scheduledMeetingIds = new Set(Object.values(agenda.scheduled).map((s) => s.meetingId));
  const manualBotMeetings = meetings.filter(
    (m) =>
      m.bot &&
      !scheduledMeetingIds.has(m.id) &&
      m.status !== 'recorded' &&
      dayKey(new Date(m.recordedAtISO)) === dayKey(new Date())
  );

  // — 2. Dün deşifre edilenler (dün yoksa en son deşifre edilen gün) —
  const recap = useMemo(() => {
    const recorded = meetings
      .filter((m) => m.status === 'recorded' && m.summary.tldr)
      .sort((a, b) => b.recordedAtISO.localeCompare(a.recordedAtISO));
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const yesterdayLabel = t('Dün deşifre edilenler', 'Transcribed yesterday');
    const fromYesterday = recorded.filter((m) => dayKey(new Date(m.recordedAtISO)) === dayKey(yesterday));
    if (fromYesterday.length > 0) return { label: yesterdayLabel, isYesterday: true, meetings: fromYesterday };

    const before = recorded.filter((m) => new Date(m.recordedAtISO) < new Date(new Date().setHours(0, 0, 0, 0)));
    if (before.length === 0) return { label: yesterdayLabel, isYesterday: true, meetings: [] };
    const lastDay = dayKey(new Date(before[0].recordedAtISO));
    return {
      label: t(
        `Son deşifre edilenler · ${formatDate(before[0].recordedAtISO)}`,
        `Last transcribed · ${formatDate(before[0].recordedAtISO)}`
      ),
      isYesterday: false,
      meetings: before.filter((m) => dayKey(new Date(m.recordedAtISO)) === lastDay),
    };
    // Etiketler seçili dilde üretiliyor; dil değişince yeniden hesaplansın.
  }, [meetings, language]);

  // — 3. Açık aksiyonlar (toplantıya göre gruplu, en yeni toplantı önce) —
  const actionGroups = useMemo(
    () =>
      meetings
        .filter((m) => m.actionItems.some((a) => !a.completed))
        .sort((a, b) => b.recordedAtISO.localeCompare(a.recordedAtISO))
        .map((m) => ({ meeting: m, items: m.actionItems.filter((a) => !a.completed) })),
    [meetings]
  );
  const openActionCount = actionGroups.reduce((sum, g) => sum + g.items.length, 0);
  const visibleGroups = showAllActions ? actionGroups : actionGroups.slice(0, 6);

  const upcomingToday = agenda.events.filter((e) => !e.isAllDay && Date.parse(e.endISO) > Date.now());

  /** Bot bu toplantıya kendiliğinden gidecek mi (genel ayar ya da toplantının kendi seçimi). */
  const willAutoJoin = (event: CalendarEventDTO) =>
    canSendBot(event) && isBotJoinable(event) && wantsAutoJoin(event, agenda.prefs);

  const toggleAction = (meeting: Meeting, itemId: string) => {
    onUpdateMeeting({
      ...meeting,
      actionItems: meeting.actionItems.map((a) => (a.id === itemId ? { ...a, completed: !a.completed } : a)),
    });
  };

  const handleSendBot = async (event: CalendarEventDTO) => {
    setBusyEventId(event.id);
    try {
      await agenda.sendBot(event);
      onNotify(
        'success',
        Date.parse(event.startISO) - Date.now() > 60_000
          ? t(`Bot "${event.title}" başlayınca katılacak.`, `The bot will join "${event.title}" when it starts.`)
          : t(`Bot "${event.title}" toplantısına katılıyor.`, `The bot is joining "${event.title}".`)
      );
    } catch (err) {
      onNotify('error', (err as Error).message || t('Bot gönderilemedi.', 'Could not send the bot.'));
    } finally {
      setBusyEventId(null);
    }
  };

  const handleEventJoin = async (event: CalendarEventDTO, join: boolean) => {
    setBusyEventId(event.id);
    try {
      await agenda.setEventJoin(event, join);
    } catch (err) {
      onNotify('error', (err as Error).message || t('Tercih kaydedilemedi.', 'Could not save your preference.'));
    } finally {
      setBusyEventId(null);
    }
  };

  const handleCopy = () => {
    const lines = [
      t(
        `# Günlük Özet — ${new Date().toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long', year: 'numeric' })}`,
        `# Daily Digest — ${new Date().toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long', year: 'numeric' })}`
      ),
      '',
      t('## Bugünün Programı', '## Today’s schedule'),
      ...(agenda.events.length
        ? agenda.events.map((e) => `- ${e.isAllDay ? t('Tüm gün', 'All day') : formatClock(e.startISO)} ${e.title}`)
        : [t('- Takvimde toplantı yok', '- No meetings on the calendar')]),
      '',
      `## ${recap.label}`,
      ...recap.meetings.flatMap((m) => [`### ${m.title}`, displaySummaryText(m.summary.tldr), '']),
      t('## Açık Aksiyonlar', '## Open action items'),
      ...actionGroups.flatMap((g) => g.items.map((a) => `- [ ] ${a.text}${a.assignee ? ` (${a.assignee})` : ''} — ${g.meeting.title}`)),
    ];
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }, (err) => console.error('[clipboard] kopyalanamadı:', err));
  };

  const renderBotChip = (event: CalendarEventDTO) => {
    const scheduled = agenda.scheduled[event.id];
    const excluded = agenda.prefs.autoJoinExcluded.includes(event.id);
    if (scheduled) {
      const { label, tone } = botStatusLabel({ status: scheduled.status, meetingUrl: '' });
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${BOT_TONE_CLASS[tone]}`}>
          <Bot className="w-3 h-3" />
          {label}
        </span>
      );
    }
    if (excluded) {
      return <span className="text-[11px] text-neutral-500">{t('Bot katılmayacak', 'Bot won’t join')}</span>;
    }
    if (willAutoJoin(event)) {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${BOT_TONE_CLASS.pending}`}>
          <Bot className="w-3 h-3" />
          {t('Otomatik katılacak', 'Will join automatically')}
        </span>
      );
    }
    return null;
  };

  return (
    <div className="max-w-3xl mx-auto w-full px-6 py-8 space-y-7 select-none">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5 text-[#46CBE2] text-xs font-bold uppercase tracking-wider mb-1">
            <Sparkles className="w-3.5 h-3.5" />
            <span>{t('Günlük Özet', 'Daily Digest')}</span>
          </div>
          <h1 className={pageTitle(isDarkMode)}>
            {new Date().toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long', weekday: 'long' })}
          </h1>
          <p className="text-[13px] text-neutral-500 mt-1.5">
            {t(
              'Bugünkü görüşmelerin, dünden kalan özetlerin ve açık işlerin tek bakışta.',
              'Today’s meetings, yesterday’s summaries and your open items at a glance.'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={handleCopy} className={secondaryButton(isDarkMode)}>
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copied ? t('Kopyalandı', 'Copied') : 'Markdown'}</span>
          </button>
          <button
            onClick={() =>
              onAskAI(
                t(
                  'Bugünkü programımı, dünkü toplantıların özetini ve açık aksiyonlarımı önceliklendirerek özetle',
                  'Summarize my schedule today, yesterday’s meetings and my open action items in priority order'
                )
              )
            }
            className={primaryButton}
          >
            <Sparkles className="w-3.5 h-3.5" />
            <span>{t('AI\'a sor', 'Ask AI')}</span>
          </button>
        </div>
      </div>

      {/* Sayaçlar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
        {[
          {
            label: t('Bugün kalan toplantı', 'Meetings left today'),
            value: upcomingToday.length + manualBotMeetings.length,
            hint: t(`${agenda.events.length} etkinlik takvimde`, `${plural(agenda.events.length, 'event', 'events')} on the calendar`),
          },
          {
            label: recap.isYesterday ? t('Dünün toplantıları', 'Yesterday’s meetings') : t('Son deşifreler', 'Latest transcripts'),
            value: recap.meetings.length,
            hint: t('özetlendi', 'summarized'),
          },
          {
            label: t('Açık aksiyon', 'Open action items'),
            value: openActionCount,
            hint: t(`${actionGroups.length} toplantıdan`, `from ${plural(actionGroups.length, 'meeting', 'meetings')}`),
          },
        ].map((stat) => (
          <div key={stat.label} className={`p-4 rounded-2xl border ${card}`}>
            <div className="text-[11px] text-neutral-400 font-semibold mb-1">{stat.label}</div>
            <div className="text-2xl font-bold text-[#F0EEE6] font-mono">{stat.value}</div>
            <div className="text-[11px] text-neutral-500 mt-1">{stat.hint}</div>
          </div>
        ))}
      </div>

      {/* 1. Bugünün programı */}
      <section className="space-y-2.5">
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          <CalendarClock className="w-3.5 h-3.5 text-[#46CBE2]" />
          {t('Bugünün Programı', 'Today’s schedule')}
          {autoJoinOn && (
            <span className="normal-case tracking-normal font-medium text-[#46CBE2]">
              · {t('otomatik bot açık', 'auto-join on')}
            </span>
          )}
        </h2>

        <div className={`rounded-2xl border divide-y ${card} ${isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100'}`}>
          {agenda.isLoading && (
            <p className="p-4 text-[13px] text-neutral-500 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('Takvim yükleniyor…', 'Loading calendar…')}
            </p>
          )}
          {!agenda.isLoading && agenda.calendarError && (
            <p className="p-4 text-[13px] text-amber-400">{agenda.calendarError}</p>
          )}
          {!agenda.isLoading && !agenda.calendarError && agenda.events.length === 0 && manualBotMeetings.length === 0 && (
            <p className="p-4 text-[13px] text-neutral-500">{t('Bugün takviminde toplantı yok.', 'No meetings on your calendar today.')}</p>
          )}

          {agenda.events.map((event) => {
            const ended = Date.parse(event.endISO) <= Date.now();
            const scheduled = agenda.scheduled[event.id];
            const excluded = agenda.prefs.autoJoinExcluded.includes(event.id);
            const busy = busyEventId === event.id;
            const linkedMeeting = scheduled ? meetingById.get(scheduled.meetingId) : undefined;
            return (
              <div key={event.id} className={`p-4 flex items-start justify-between gap-3 ${ended ? 'opacity-50' : ''}`}>
                <div className="flex items-start gap-3 min-w-0">
                  <div className="text-[12px] font-mono text-neutral-400 w-[92px] shrink-0 pt-0.5">
                    {event.isAllDay ? t('Tüm gün', 'All day') : `${formatClock(event.startISO)} – ${formatClock(event.endISO)}`}
                  </div>
                  <div className="min-w-0 space-y-1">
                    <div className="text-[14px] font-semibold text-[#F0EEE6] truncate">{event.title}</div>
                    <div className="flex items-center gap-2 flex-wrap text-[11.5px] text-neutral-500">
                      {event.conference && (
                        <span className="flex items-center gap-1">
                          <Video className="w-3 h-3" /> {event.conference.label}
                        </span>
                      )}
                      {event.attendees && event.attendees.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />{' '}
                          {t(`${event.attendees.length} katılımcı`, plural(event.attendees.length, 'participant', 'participants'))}
                        </span>
                      )}
                      <span className="uppercase text-[10px] tracking-wider">{event.source === 'outlook' ? 'Outlook' : 'Google'}</span>
                      {renderBotChip(event)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {linkedMeeting && (
                    <button onClick={() => onSelectMeeting(linkedMeeting)} className={secondaryButton(isDarkMode)}>
                      {t('Aç', 'Open')}
                    </button>
                  )}
                  {!scheduled && !excluded && canSendBot(event) && (
                    <button onClick={() => void handleSendBot(event)} disabled={busy} className={primaryButton}>
                      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bot className="w-3.5 h-3.5" />}
                      {t('Bot gönder', 'Send bot')}
                    </button>
                  )}
                  {!ended && canSendBot(event) && (scheduled || excluded || willAutoJoin(event)) && (
                    <button
                      onClick={() => void handleEventJoin(event, excluded)}
                      disabled={busy}
                      title={excluded ? t('Bot bu toplantıya yine katılabilsin', 'Let the bot join this meeting again') : t('Bot bu toplantıya katılmasın', 'Don’t let the bot join this meeting')}
                      className="text-[12px] font-medium text-neutral-500 hover:text-neutral-300 px-2 py-1.5"
                    >
                      {excluded ? t('Geri al', 'Undo') : t('Katılmasın', 'Don’t join')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {manualBotMeetings.map((meeting) => (
            <button
              key={meeting.id}
              onClick={() => onSelectMeeting(meeting)}
              className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-white/[0.02]"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="text-[12px] font-mono text-neutral-400 w-[92px] shrink-0">{meeting.time}</div>
                <div className="text-[14px] font-semibold text-[#F0EEE6] truncate">{meeting.title}</div>
              </div>
              {meeting.bot && (() => {
                const { label, tone } = botStatusLabel(meeting.bot);
                return (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${BOT_TONE_CLASS[tone]}`}>
                    <Bot className="w-3 h-3" />
                    {label}
                  </span>
                );
              })()}
            </button>
          ))}
        </div>
      </section>

      {/* 2. Dün deşifre edilenler */}
      <section className="space-y-2.5">
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          <FileText className="w-3.5 h-3.5 text-amber-400" />
          {recap.label}
        </h2>
        {recap.meetings.length === 0 ? (
          <div className={`p-4 rounded-2xl border text-[13px] text-neutral-500 ${card}`}>
            {t('Henüz özetlenmiş bir toplantı yok.', 'No summarized meetings yet.')}
          </div>
        ) : (
          recap.meetings.map((meeting) => (
            <button
              key={meeting.id}
              onClick={() => onSelectMeeting(meeting)}
              className={`w-full text-left p-4 rounded-2xl border transition-all group hover:border-[#3AC8E0]/40 ${card}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-1.5">
                  <h3 className="font-bold text-[14px] text-[#F0EEE6] truncate group-hover:text-[#46CBE2] transition-colors">
                    {meeting.title}
                  </h3>
                  <p className="text-[12.5px] text-neutral-400 leading-relaxed line-clamp-3">{displaySummaryText(meeting.summary.tldr)}</p>
                  {meeting.summary.keyPoints.slice(0, 2).map((point, i) => (
                    <p key={i} className="text-[12px] text-neutral-500 flex items-start gap-2">
                      <span className="w-1 h-1 rounded-full bg-[#46CBE2] shrink-0 mt-2" />
                      <span className="line-clamp-1">{point}</span>
                    </p>
                  ))}
                </div>
                <div className="flex items-center gap-2 shrink-0 text-[12px] font-mono text-neutral-500">
                  <span>{meeting.time}</span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </button>
          ))
        )}
      </section>

      {/* 3. Aksiyon adımları */}
      <section className="space-y-2.5">
        <h2 className={`${sectionTitle} flex items-center gap-2`}>
          <ListChecks className="w-3.5 h-3.5 text-emerald-400" />
          {t('Aksiyon Adımları', 'Action items')}
        </h2>
        {actionGroups.length === 0 ? (
          <div className={`p-4 rounded-2xl border text-[13px] text-neutral-500 flex items-center gap-2 ${card}`}>
            <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {t('Açık aksiyon yok — her şey tamam.', 'No open action items — you’re all caught up.')}
          </div>
        ) : (
          <div className={`rounded-2xl border divide-y ${card} ${isDarkMode ? 'divide-[#222228]' : 'divide-neutral-100'}`}>
            {visibleGroups.map(({ meeting, items }) => (
              <div key={meeting.id} className="p-4 space-y-2">
                <button
                  onClick={() => onSelectMeeting(meeting)}
                  className="text-[12px] font-semibold text-neutral-400 hover:text-[#46CBE2] transition-colors flex items-center gap-1.5"
                >
                  {meeting.title}
                  <span className="font-normal text-neutral-600">· {meeting.date}</span>
                </button>
                {items.map((item) => (
                  <div key={item.id} className="flex items-start justify-between gap-3">
                    <button
                      onClick={() => toggleAction(meeting, item.id)}
                      className="flex items-start gap-2.5 text-left min-w-0 group"
                      title={t('Tamamlandı olarak işaretle', 'Mark as done')}
                    >
                      <Circle className="w-4 h-4 text-neutral-500 group-hover:text-emerald-400 shrink-0 mt-0.5" />
                      <span className="text-[13px] text-neutral-300 leading-relaxed">{item.text}</span>
                    </button>
                    <div className="text-[11px] text-neutral-500 shrink-0 text-right">
                      {item.assignee && <div>{item.assignee}</div>}
                      {item.dueDate && <div className="font-mono">{item.dueDate}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
            {actionGroups.length > 6 && (
              <button
                onClick={() => setShowAllActions((v) => !v)}
                className="w-full p-3 text-[12px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA]"
              >
                {showAllActions
                  ? t('Daha az göster', 'Show less')
                  : t(`Tümünü göster (${actionGroups.length} toplantı)`, `Show all (${plural(actionGroups.length, 'meeting', 'meetings')})`)}
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
};
